import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
// hooks
import { useFormsLinking } from 'hooks/risks'
// types
import { IFieldFilter, IOrderInput } from 'types/graphql'
// constants
import { RISKS_PER_PAGE } from 'constants/table'
// context
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// utils
import { safeJsonParse } from 'utils/helpers'

export const useRiskViewTable = () => {
  const router = useRouter()

  const { riskView } = React.useContext(RiskRegisterContext)
  const { isRiskTemplate, isRisk } = React.useContext(RiskTemplateAPIContext)

  const { isFormView } = useFormsLinking()

  const initialValues = React.useMemo(() => {
    if (router.query.riskViewId || isFormView) {
      return {
        tab: null,
        page: 0,
        order: null,
        filters: null,
        perPage: RISKS_PER_PAGE[0]
      }
    }

    const order = safeJsonParse<IOrderInput>(localStorage.getItem('order'))
    const filters = safeJsonParse<IFieldFilter[]>(localStorage.getItem('filter'))

    return {
      order,
      filters,
      tab: localStorage.getItem('filterId'),
      page: Number(localStorage.getItem('page')) || 0,
      perPage: Number(localStorage.getItem('perPage')) || RISKS_PER_PAGE[0]
    }
  }, [router.query.riskViewId, isFormView])

  const [page, setPage] = React.useState(() => initialValues.page)
  const [order, setOrder] = React.useState<IOrderInput | null>(() => initialValues.order)
  const [search, setSearch] = React.useState(null)
  const [perPage, setPerPage] = React.useState(() => initialValues.perPage)
  const [currentTab, setCurrentTab] = React.useState<string | null>(() => initialValues.tab)
  const [appliedFilters, setAppliedFilters] = React.useState<IFieldFilter[] | null>(() => initialValues.filters)
  const [isInfiniteLoader, setIsInfiniteLoader] = React.useState(false)

  const prevRiskViewRef = React.useRef(null)

  const storeTableParams = (params: { key: string; value: any }[]) => {
    if (isRiskTemplate || isRisk) {
      return
    }

    params.forEach((item) => localStorage.setItem(item.key, item.value))
  }

  const handleSetOrder = (order: IOrderInput) => {
    setPage(0)
    setOrder(order)

    storeTableParams([
      { key: 'page', value: 0 },
      { key: 'order', value: JSON.stringify(order) }
    ])
  }

  const handleSetPerPage: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = ({ target }) => {
    const perPage = +target?.value || RISKS_PER_PAGE[0]

    setPage(0)
    setPerPage(perPage)

    storeTableParams([
      { key: 'page', value: 0 },
      { key: 'perPage', value: perPage }
    ])
  }

  const handleSetAppliedFilters = (filter: IFieldFilter[] | null, filterId?: string) => {
    setPage(0)
    setAppliedFilters(filter)

    storeTableParams([
      { key: 'page', value: 0 },
      {
        key: 'filter',
        value: filter ? JSON.stringify(filter) : null
      },
      { key: 'filterId', value: filter ? filterId || currentTab : null }
    ])
  }

  const handleSetCurrentTab = (value: string | null) => {
    setCurrentTab(value)
  }

  const onPageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    setPage(page)
    storeTableParams([{ key: 'page', value: page }])
  }

  const resetTable = () => {
    setPage(0)
    setOrder(null)
    setCurrentTab(null)
    setPerPage(RISKS_PER_PAGE[0])
    setAppliedFilters(null)
  }

  // reset states on riskView change
  React.useEffect(() => {
    const prevRiskViewId = prevRiskViewRef.current?.id

    if (prevRiskViewId && riskView?.id !== prevRiskViewId) {
      resetTable()
    }

    if (isRiskTemplate) {
      resetTable()
    }

    prevRiskViewRef.current = riskView
  }, [riskView?.id, isRiskTemplate])

  return {
    page,
    order,
    search,
    perPage,
    setSearch,
    currentTab,
    onPageChange,
    appliedFilters,
    handleSetOrder,
    isInfiniteLoader,
    handleSetPerPage,
    handleSetCurrentTab,
    setIsInfiniteLoader,
    handleSetAppliedFilters
  }
}
